import CircleCloseIcon from "@/assets/common/ic_close_circle.svg?react";
import ArrowLeftIcon from "@/assets/common/ic_arrow_left.svg?react";
import ArrowRightIcon from "@/assets/common/ic_arrow_right.svg?react";
import SearchIcon from "@/assets/summary/search.svg?react";
import { useCallback, useEffect, useRef, useState } from "react";
import Mark from "mark.js";
import recordStore, { IMarkMap } from "@/store/record.store";
import { expandArray } from "@/utils/expandArray";
import { debounce } from "@common/utils";

export interface ISearchBarProps {
  className?: string;
  onCancel?: () => void;
  querySelector: string;
  trigger?: "input" | "enter";
  onSearch?: () => void;
  onJumpTo?:(index: number) => void;
}
const SearchBar: React.FC<ISearchBarProps> = (props) => {
  const { className = "", onCancel, trigger = "enter", onSearch, onJumpTo } = props;
  const markRef = useRef<Mark | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [totalIndex, setTotalIndex] = useState(0);
  const [active, setActive] = useState(true);
  const inpuRef = useRef<HTMLInputElement | null>(null);
  const markMapRef = useRef<IMarkMap>({});
  const styleRef = useRef<HTMLStyleElement | null>();


  useEffect(() => {
    if (trigger === "input") {
      updateMark();
    }
  }, [searchValue]);

  useEffect(() => {
    return () => {
      const style = styleRef.current;
      if (style && style.parentNode) {
        style.parentNode.removeChild(style);
      } else {
        console.log("Style node not found");
      }
      styleRef.current = null;
    };
  }, []);

  const resetNum = useCallback(() => {
    setCurrentIndex(0);
    setTotalIndex(0);
  }, []);

  const unmark = () => {
    // 清空记录表
    markMapRef.current = {};
    const records = recordStore.getState().records;
    const newRecords = records.map(record=>{
      record.sc = record.sc.map(sItem=>{
        sItem.words = sItem.words.map(wItem => {
          wItem.formatWord = "";
          return wItem;
        });
        return sItem;
      })
      return record;
    });
    recordStore.getState().setMarkMap({});
    recordStore.getState().setRecords(newRecords);
  }

  function findKeywordIndices(text: string, keyword: string) {
    const indices = [];
    let startIndex = 0;
  
    while (startIndex < text.length) {
      const index = text.indexOf(keyword, startIndex);
      if (index === -1) {
        break;
      }
      indices.push(index);
      startIndex = index + keyword.length;
    }
  
    return indices;
  }

  const updateMark = () => {
    unmark();
    resetNum();
    // 记录在数据中所有标识分别在哪个段落，然后根据定位到具体元素
    if(!searchValue) return;
    const records = recordStore.getState().records;
    const markMap: IMarkMap = {};
    const recordsContent = records.flatMap((record) => record.sc)?.flatMap((scItem) => scItem.words).map((wordItem) => wordItem.word).join("");
    const indices = findKeywordIndices(recordsContent, searchValue);
    
    let totalWIndex = 0;
    let totalCIndex = 0;
    const formatIndices = expandArray(indices, searchValue.length);
    const newRecords = records.map(record=>{
      record.sc = record.sc.map(sItem=>{
        sItem.words = sItem.words.map(wItem => {
          let formatWord = wItem.word.split("");
          formatWord = formatWord.map((cItem)=>{
            let targetMarkId = -1;
            let formatChar = cItem;
            if(formatIndices.includes(totalCIndex)){
              const targetItem = indices.find((item, index)=>{
                if(totalCIndex >= item && totalCIndex <= item + searchValue.length - 1){
                  targetMarkId = index+1;
                  return true;
                }
                return false;
              });
              if(targetItem !== undefined){
                formatChar = `<mark data-mark-id="mark-${targetMarkId}" class="bg-[rgba(73,129,242,0.2)] mark-${targetMarkId}">${formatChar}</mark>`;
              }
            }
            totalCIndex++;
            return formatChar;
          });
          wItem.formatWord = formatWord.join("");
          return wItem;
        });
        return sItem;
      });
      const recordContent = record?.sc?.flatMap((scItem) => scItem.words).map((wordItem) => wordItem.word).join("");
      totalWIndex += recordContent.length;
      record.endWIndex = totalWIndex - 1;
      return record;
    });

    indices.forEach((item, index)=>{
      let targetPIndex = -1;
      const targetRecord = newRecords.find((record, recordIndex)=>{
        if(record.endWIndex && item <= record.endWIndex){
          targetPIndex = recordIndex;
          return record;
        }
      });
      if(targetRecord){
        markMap[index+1] = {
          pi: targetRecord.pi,
          pIndex: targetPIndex,
          markId: `mark-${index+1}`,
        };
      }
    });

    markMapRef.current = markMap;
    recordStore.getState().setMarkMap(markMap);
    recordStore.getState().setRecords(newRecords);
    setTotalIndex(indices.length);
    if(indices.length < 1){
      return;
    }
    setCurrentIndex(1);
    jumpTo(1);
  };

  const debounceHandlePrev = debounce(() => {
    handlePrev();
  }, 200);
  const debounceHandleNext = debounce(() => {
    handleNext();
  }, 200);

  const updateStyle = (index: number) => {
    const css = `
      .mark-${index} {
        background: #FFD56A
      }`;
    if (!styleRef.current) {
      const styleEle = document.createElement("style");
      styleEle.textContent = css;
      document.head.appendChild(styleEle);
      styleRef.current = styleEle;
    }
    styleRef.current.textContent = css;
  };

  const jumpTo = async (index: number) => {
    // 使用 style节点来更新当前高亮元素
    updateStyle(index);
    onSearch && onSearch();

    const targetSen = markMapRef.current[index];
    if(!targetSen) return;
    const {pIndex, markId} = targetSen;
    if (pIndex < 0) return;

    // 跳转到目标段落
    onJumpTo && await onJumpTo(pIndex);

    const targetEle = document?.querySelector(`[data-mark-id="${markId}"]`);
    if(targetEle && searchValue.includes(targetEle.innerHTML)){
      setTimeout(() => {
        targetEle.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }, 0);
      }
  };

  const handleInputChange = (val: string) => {
    setSearchValue(val);
  };
  const handlePrev = () => {
    if(!Object.keys(markMapRef.current).length) return;
    if (currentIndex <= 1) {
      jumpTo(totalIndex);
      setCurrentIndex(totalIndex);
    } else {
      jumpTo(currentIndex - 1);
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if(!Object.keys(markMapRef.current).length) return;
    if (currentIndex === totalIndex) {
      jumpTo(1);
      setCurrentIndex(1);
    } else {
      jumpTo(currentIndex + 1);
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handleHideSearch = () => {
    markRef.current?.unmark();
    unmark();
    onCancel?.();
  };

  const handleSearchBlur = () => {
    if (!inpuRef.current?.value) {
      handleHideSearch();
    }
  };

  return (
    <div
      onClick={() => {
        setActive(true);
        inpuRef.current?.focus();
      }}
      onBlur={() => {
        setActive(false);
      }}
      className={`
        ${className}
        min-w-75 h-8 rounded-2.5 
        flex items-center font-400
        b-1 b-solid 
        ${active ? " b-mx-primary" : "b-#ECEEF3"}
      `}
    >
      <SearchIcon className="size-4 mx-2" />
      <input
        ref={inpuRef}
        onChange={(e) => {
          handleInputChange(e.target.value);
        }}
        onFocus={() => {
          setActive(true);
        }}
        onBlur={handleSearchBlur}
        value={searchValue}
        autoFocus
        className="b-0 outline-0 text-3 text-mx-secondary w-31.5 mr-2"
        onKeyDown={(e) => {
          if (e.key === "Enter" && trigger === "enter") {
            updateMark();
          }
        }}
      />
      <CircleCloseIcon
        className="size-4 cursor-pointer mr-1.25"
        onClick={() => {
          markRef.current?.unmark();
          unmark();
          setSearchValue("");
          resetNum();
        }}
      />
      <div className="w-0.25 h-4 bg-#ECEEF3 mr-2" />
      <ArrowLeftIcon onClick={debounceHandlePrev} className="size-4 mr-0.5 active:bg-#F4F6F9 active:rounded-1" />
      <div className="text-3 text-mx-quaternary">
        <span className="text-mx-secondary">{currentIndex}</span>/<span>{totalIndex}</span>
      </div>
      <ArrowRightIcon onClick={debounceHandleNext} className="size-4 ml-0.5 active:bg-#F4F6F9 active:rounded-1" />
      <div className="w-0.25 h-4 bg-#ECEEF3 ml-2" />
      <div
        className="text-mx-quaternary text-3 ml-1.25 cursor-pointer whitespace-nowrap"
        onClick={() => {
          handleHideSearch();
        }}
      >
        取消
      </div>
    </div>
  );
};

export default SearchBar;
